// src/pages/Orders/OrderDetail.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import Toast from '../../components/Toast/Toast';
import './OrderPages.css';
import './OrderDetail.css';

const STATUS_COLORS = {
  DRAFT:                { bg: '#f8f9fa', color: '#6c757d' },
  PENDING_CONFIRMATION: { bg: '#fff8e1', color: '#f57f17' },
  CONFIRMED:            { bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED:             { bg: '#fce4ec', color: '#c62828' },
  PARTIALLY_DISPATCHED: { bg: '#fff3e0', color: '#e65100' },
  DISPATCHED:           { bg: '#e3f2fd', color: '#1565c0' },
  DELIVERED:            { bg: '#e8f5e9', color: '#1b5e20' },
  FAILED_TO_DELIVER:    { bg: '#fce4ec', color: '#c62828' },
  CANCELLED:            { bg: '#f5f5f5', color: '#757575' },
};

const ITEM_STATUS_COLORS = {
  PENDING:              { bg: '#fff8e1', color: '#f57f17' },
  PARTIALLY_DISPATCHED: { bg: '#fff3e0', color: '#e65100' },
  DISPATCHED:           { bg: '#e8f5e9', color: '#2e7d32' },
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const StatusBadge = ({ status, colors }) => {
  const style = colors[status] || { bg: '#f8f9fa', color: '#6c757d' };
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: style.bg, color: style.color,
      whiteSpace: 'nowrap',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const OrderDetail = () => {
  const navigate    = useNavigate();
  const { id }      = useParams();
  const { user }    = useAuth();

  const [order,        setOrder]        = useState(null);
  const [dispatchLogs, setDispatchLogs] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);
  const [activeTab,    setActiveTab]    = useState('items'); // 'items' | 'logs'

  useEffect(() => {
    fetchOrder();
    fetchDispatchLogs();
  }, [id]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await orderService.getOrderById(id);
      if (res.success) setOrder(res.data);
    } catch (_) {
      setToast({ message: 'Failed to fetch order details', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDispatchLogs = async () => {
    try {
      const res = await orderService.getDispatchLogs(id);
      if (res.success) setDispatchLogs(res.data || []);
    } catch (_) {}
  };

  if (loading) {
    return (
      <div className="order-page-container">
        <div className="order-loading">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-page-container">
        <div className="order-loading">Order not found.</div>
      </div>
    );
  }

  const orderStatus = STATUS_COLORS[order.orderStatus] || STATUS_COLORS.DRAFT;

  return (
    <div className="order-page-container" style={{ maxWidth: 900 }}>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="order-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <button
          style={{ background: 'none', border: 'none', color: '#4a8c2a', cursor: 'pointer', fontSize: 13, padding: 0 }}
          onClick={() => navigate(-1)}
        >
          Orders
        </button>
        <span>›</span>
        <span>{order.orderId}</span>
      </nav>

      {/* Header */}
      <div className="od-header">
        <div>
          <h4 className="order-title">{order.orderId}</h4>
          <p className="order-subtitle">
            Placed on {formatDate(order.orderDate)}
            {order.createdByUser && (
              <> · by <strong>{order.createdByUser.firstName} {order.createdByUser.lastName}</strong></>
            )}
          </p>
        </div>
        <StatusBadge status={order.orderStatus} colors={STATUS_COLORS} />
      </div>

      {/* Rejection reason banner */}
      {order.orderStatus === 'REJECTED' && order.rejectionReason && (
        <div className="od-rejection-banner">
          <span>❌</span>
          <div>
            <strong>Order Denied</strong>
            <p>Reason: {order.rejectionReason}</p>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="od-info-grid">
        {/* Dealer */}
        <div className="od-info-card">
          <p className="od-info-label">Dealer</p>
          <p className="od-info-value">{order.dealer?.dealerShopName || '—'}</p>
          <p className="od-info-sub">{order.dealer?.ownerName}</p>
          <p className="od-info-sub">{order.dealer?.mobile}</p>
        </div>

        {/* Godown */}
        <div className="od-info-card">
          <p className="od-info-label">Godown</p>
          <p className="od-info-value">{order.godown?.name || '—'}</p>
          <p className="od-info-sub">{order.godown?.location}</p>
        </div>

        {/* Order Summary */}
        <div className="od-info-card">
          <p className="od-info-label">Order Summary</p>
          <p className="od-info-value">
            ₹{Number(order.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
          <p className="od-info-sub">{order.items?.length || 0} product{order.items?.length !== 1 ? 's' : ''}</p>
          {order.transportVia && (
            <p className="od-info-sub">via {order.transportVia.replace(/_/g, ' ')}</p>
          )}
        </div>

        {/* Timeline */}
        <div className="od-info-card">
          <p className="od-info-label">Timeline</p>
          <div className="od-timeline">
            <div className="od-tl-row">
              <span className="od-tl-dot active" />
              <div>
                <span className="od-tl-label">Created</span>
                <span className="od-tl-date">{formatDateTime(order.createdAt)}</span>
              </div>
            </div>
            {order.confirmedAt && (
              <div className="od-tl-row">
                <span className="od-tl-dot active green" />
                <div>
                  <span className="od-tl-label">Confirmed</span>
                  <span className="od-tl-date">{formatDateTime(order.confirmedAt)}</span>
                  {order.confirmedByUser && (
                    <span className="od-tl-by">
                      by {order.confirmedByUser.firstName} {order.confirmedByUser.lastName}
                    </span>
                  )}
                </div>
              </div>
            )}
            {order.dispatchedAt && (
              <div className="od-tl-row">
                <span className="od-tl-dot active blue" />
                <div>
                  <span className="od-tl-label">Dispatched</span>
                  <span className="od-tl-date">{formatDateTime(order.dispatchedAt)}</span>
                </div>
              </div>
            )}
            {order.deliveredAt && (
              <div className="od-tl-row">
                <span className="od-tl-dot active green" />
                <div>
                  <span className="od-tl-label">Delivered</span>
                  <span className="od-tl-date">{formatDateTime(order.deliveredAt)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="od-notes">
          <span className="od-notes-label">📝 Notes:</span> {order.notes}
        </div>
      )}

      {/* Tabs */}
      <div className="order-tabs" style={{ marginTop: 24 }}>
        <button
          className={`order-tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          Products ({order.items?.length || 0})
        </button>
        <button
          className={`order-tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Dispatch Logs ({dispatchLogs.length})
        </button>
      </div>

      {/* Products Tab */}
      {activeTab === 'items' && (
        <div className="order-table-container">
          <table className="order-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Ordered Units</th>
                <th>Ordered Boxes</th>
                <th>Dispatched</th>
                <th>Pending</th>
                <th>Rate/Unit</th>
                <th>Rate/Box</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td style={{ fontWeight: 500 }}>
                    {item.product?.productName || '—'}
                    {item.product?.productAndPacking && (
                      <div style={{ fontSize: 12, color: '#6c757d' }}>
                        {item.product.productAndPacking}
                      </div>
                    )}
                  </td>
                  <td>{item.orderedUnits || '—'}</td>
                  <td>{item.orderedBoxes || '—'}</td>
                  <td style={{ color: '#2e7d32', fontWeight: 500 }}>
                    {item.dispatchedUnits > 0 && <span>{item.dispatchedUnits} units </span>}
                    {item.dispatchedBoxes > 0 && <span>{item.dispatchedBoxes} boxes</span>}
                    {item.dispatchedUnits === 0 && item.dispatchedBoxes === 0 && '—'}
                  </td>
                  <td style={{ color: item.pendingUnits > 0 || item.pendingBoxes > 0 ? '#e65100' : '#2e7d32', fontWeight: 500 }}>
                    {item.pendingUnits > 0 && <span>{item.pendingUnits} units </span>}
                    {item.pendingBoxes > 0 && <span>{item.pendingBoxes} boxes</span>}
                    {item.pendingUnits === 0 && item.pendingBoxes === 0 && '✓'}
                  </td>
                  <td>₹{Number(item.ratePerUnit).toLocaleString('en-IN')}</td>
                  <td>₹{Number(item.ratePerBox).toLocaleString('en-IN')}</td>
                  <td style={{ fontWeight: 600 }}>
                    ₹{Number(item.itemTotalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td>
                    <StatusBadge status={item.itemStatus} colors={ITEM_STATUS_COLORS} />
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr className="od-total-row">
                <td colSpan="8" style={{ textAlign: 'right', fontWeight: 600, paddingRight: 16 }}>
                  Order Total
                </td>
                <td style={{ fontWeight: 700, color: '#2e7d32', fontSize: 15 }}>
                  ₹{Number(order.totalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Dispatch Logs Tab */}
      {activeTab === 'logs' && (
        <div className="order-table-container">
          {dispatchLogs.length > 0 ? (
            <table className="order-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Units Dispatched</th>
                  <th>Boxes Dispatched</th>
                  <th>Transport</th>
                  <th>Dispatched By</th>
                  <th>Dispatched At</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {dispatchLogs.map((log, index) => (
                  <tr key={log.id}>
                    <td>{index + 1}</td>
                    <td>{log.orderItem?.product?.productName || '—'}</td>
                    <td style={{ color: '#2e7d32', fontWeight: 500 }}>
                      {log.dispatchedUnits || '—'}
                    </td>
                    <td style={{ color: '#2e7d32', fontWeight: 500 }}>
                      {log.dispatchedBoxes || '—'}
                    </td>
                    <td>{log.transportVia?.replace(/_/g, ' ') || '—'}</td>
                    <td>
                      {log.dispatchedByUser?.firstName} {log.dispatchedByUser?.lastName}
                    </td>
                    <td style={{ fontSize: 13 }}>{formatDateTime(log.dispatchedAt)}</td>
                    <td style={{ fontSize: 13, color: '#6c757d' }}>{log.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="order-no-data" style={{ padding: 40 }}>
              No dispatch logs yet
            </div>
          )}
        </div>
      )}

      {/* Back button */}
      <div style={{ marginTop: 24 }}>
        <button className="order-btn-secondary" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    </div>
  );
};

export default OrderDetail;